import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import MapModel from '../../models/Map';
import { LAYER_TYPES } from '../../factories/LayerFactory';
import Constants from '../../utils/constants';
import FeatureDisplayer from './FeatureDisplayer';
import { Vector } from 'ol/layer';
import { GeoJSON } from 'ol/format';
import Overlay from 'ol/Overlay';

// Identify hit radii (screen/request pixels). The hover pre-check (getData) turns
// the cursor into a hand when a rendered symbol pixel is within HOVER_RADIUS_PX of
// the cursor. The server-side GetFeatureInfo search radius (QUERY_TOLERANCE_PX)
// must be LARGER — by at least the symbol's visual radius — so that whenever the
// cursor shows a hand the query reliably returns a feature; otherwise the cursor
// promises something the query (searching from the click to the feature centre)
// then misses. Keep QUERY_TOLERANCE_PX ≥ HOVER_RADIUS_PX + symbol radius.
const HOVER_RADIUS_PX = 8;
const QUERY_TOLERANCE_PX = 12;

// Whether a raster/tile layer has a rendered (non-transparent) pixel within
// HOVER_RADIUS_PX (euclidean) of the pixel — sampled on a fine grid so small
// point symbols aren't missed, and bounded to a circle so the detection reach is
// well-defined (and stays under QUERY_TOLERANCE_PX).
const isRasterHit = (olLayer, [px, py]) => {
  const r2 = HOVER_RADIUS_PX * HOVER_RADIUS_PX;
  for (let dx = -HOVER_RADIUS_PX; dx <= HOVER_RADIUS_PX; dx += 2) {
    for (let dy = -HOVER_RADIUS_PX; dy <= HOVER_RADIUS_PX; dy += 2) {
      if (dx * dx + dy * dy > r2) {
        continue;
      }
      const data = olLayer.getData([px + dx, py + dy]);
      if (data && data[3] > 0) {
        return true;
      }
    }
  }
  return false;
};

// A layer is "under" the click if a vector feature is at the coordinate or the
// raster has content within tolerance of the pixel.
const isLayerAtPixel = (layer, pixel, coordinate) => {
  if (layer.olLayer instanceof Vector) {
    return layer.olLayer.getSource().getFeaturesAtCoordinate(coordinate).length > 0;
  }
  return isRasterHit(layer.olLayer, pixel);
};

export default function GetFeatureInfo({ map }) {
  const [features, setFeatures] = useState([]);
  const [htmlBlocks, setHtmlBlocks] = useState([]);
  const selectFeatureTooltip = useRef(null)
  const selectFeatureTooltipElement = useRef(null);

  const overlayLayers = map.layers.filter((layer) => layer.type === LAYER_TYPES.OVERLAY && layer.olLayer.isVisible());

  // Generate manually the link to get features from layer
  const getWFSUrl = (service_url,coordinate, typename, srsname) =>  {
    const margin = 0.01;
    const lon = coordinate[0];
    const lat = coordinate[1];
    const minLon = lon - margin;
    const maxLon = lon + margin;
    const minLat = lat - margin;
    const maxLat = lat + margin;
    const bbox = [minLon, minLat, maxLon, maxLat].join(',');
    return `${service_url}?service=WFS&request=GetFeature&typename=${typename}&outputFormat=application/json&srsname=${srsname}&bbox=${bbox},${srsname}`;
  }

  const createHelpTooltip = () => {
    if (selectFeatureTooltipElement.current) {
      selectFeatureTooltipElement.current.parentNode.removeChild(selectFeatureTooltipElement.current);
    }
    selectFeatureTooltipElement.current = document.createElement('div');
    selectFeatureTooltipElement.current.className = 'tooltip hidden';
    selectFeatureTooltip.current = new Overlay({
      element: selectFeatureTooltipElement.current,
      offset: [15, 0],
      positioning: 'center-left',
      stopEvent: false,
    });
    map.olMap.addOverlay(selectFeatureTooltip.current);
  };


  const handleMapClick = (event) => {
    const view = map.olMap.getView();
    const pixel = map.olMap.getEventPixel(event.originalEvent);
    const viewResolution = view.getResolution();
    const layersAtPixel = overlayLayers.filter((layer) => isLayerAtPixel(layer, pixel, event.coordinate));

    if (layersAtPixel.length === 0) {
      setFeatures([]);
      setHtmlBlocks([]);
      return;
    }

    const getFeatureInfoPromises = layersAtPixel
      .map((layer) => {

        if (layer.olLayer instanceof Vector) {
          return getWFSUrl(layer.serviceUrl,event.coordinate, layer.featureTypeName, map.olMap.getView().getProjection().getCode());
        }
        else {
          return layer
            .olLayer
            .getSource()
            .getFeatureInfoUrl(event.coordinate, viewResolution, map.olMap.getView().getProjection().getCode(), {
              INFO_FORMAT: layer.infoFormat || "application/json",
              FEATURE_COUNT: 10,
              WITH_GEOMETRY: true,
              // widen the server-side hit test so a click near (not exactly on) a
              // small symbol still returns it, and so a hand cursor reliably yields
              // a result — FI_* is QGIS Server, BUFFER GeoServer
              FI_POINT_TOLERANCE: QUERY_TOLERANCE_PX,
              FI_LINE_TOLERANCE: QUERY_TOLERANCE_PX,
              FI_POLYGON_TOLERANCE: QUERY_TOLERANCE_PX,
              BUFFER: QUERY_TOLERANCE_PX,
              // QGIS Server: include the layer's HTML map tip (the rich card) in the response
              ...(layer.infoFormat === "text/html" && { WITH_MAPTIP: true }),
            })
        }
      })
      .filter((url) => url)
      .map((url) => `${Constants.API_BASE_URL}/layers/wms-request/get-feature-info/?url=${encodeURIComponent(url)}`)
      .map((url) => axios.get(url));

    Promise.all(getFeatureInfoPromises).then((results) => {
      const newFeatures = [];
      const newHtmlBlocks = [];

      results.forEach(({ data }) => {
        if (data && data.html != null) {
          // text/html GetFeatureInfo (e.g. a QGIS Server maptip card)
          newHtmlBlocks.push(data.html);
        } else if (data && Array.isArray(data.features)) {
          newFeatures.push(...data.features);
        }
      });

      setFeatures(newFeatures);
      setHtmlBlocks(newHtmlBlocks);
    });
  };

  const handlePointerMove = (event) => {
    if (event.dragging) {
      return;
    }

    const pixel = map.olMap.getEventPixel(event.originalEvent);
    const hit = overlayLayers.some((layer) => isLayerAtPixel(layer, pixel, event.coordinate));
    selectFeatureTooltipElement.current.innerHTML = GetFeatureInfo.messages.helpTooltip;
    selectFeatureTooltip.current.setPosition(event.coordinate);

    selectFeatureTooltipElement.current.classList.remove('hidden');

    map.olMap.getTargetElement().style.cursor = hit ? "pointer" : "";
  };

  useEffect(() => {
    map.olMap.on("singleclick", handleMapClick);
    map.olMap.on("pointermove", handlePointerMove);
    createHelpTooltip()
    map.olMap.getViewport().addEventListener('mouseout', () => {
      selectFeatureTooltipElement.current.classList.add('hidden');
    });
    return () => {
      map.olMap.un("singleclick", handleMapClick);
      map.olMap.un("pointermove", handlePointerMove);
      map.olMap.getTargetElement().style.cursor = "";
    };
  }, []);

  const handleModalClose = useCallback(() => {
    setFeatures([]);
    setHtmlBlocks([]);
  }, []);

  const featureFocus = (feature) => {
    try {
      // Get geometry from feature
      const feature_geom = new GeoJSON().readGeometry(feature.geometry);
      // Focus on the feature
      map.olMap.getView().fit(feature_geom);
      // Close modal
      handleModalClose();
    }  catch (error) {
      // Error, probably because feature had no geometry
      alert(GetFeatureInfo.messages.featureFocusError);
    }
  }

  return (
    <>
      {(features.length > 0 || htmlBlocks.length > 0) &&
        <FeatureDisplayer
          features={features}
          htmlBlocks={htmlBlocks}
          featureFocus={featureFocus}
          handleModalClose={handleModalClose}
        />}
    </>
  );
}

GetFeatureInfo.propTypes = {
  map: PropTypes.instanceOf(MapModel).isRequired,
};
GetFeatureInfo.messages = {
  helpTooltip: "Obter informação de overlay layers",
  featureFocusError: "Erro ao focar, não foi possível determinar a geometria da feature",
}
